import React, { useMemo, useRef, useState } from "react"
import {
    ExpandedState,
    functionalUpdate,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    Row,
    RowSelectionState,
    SortingState,
    useReactTable,
} from "@tanstack/react-table"
import {
    TableColumn,
    TableExpandedState,
    TablePaginationState,
    TableRowData,
    TableRowSelectionState,
    TableRowSelectVisibility,
    TableSize,
    TableSortingState,
    TableStyle,
    TableVirtualizationConfig,
} from "./types"
import { hasTableAnySelectableRows, mapColumnDefs } from "./utils"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { TableHeader } from "./components/TableHeader"
import { TableBody } from "./components/TableBody"
import { LoadingOverlay } from "../LoadingOverlay/LoadingOverlay"
import { TableFooter } from "./components/TableFooter"
import { TablePagination } from "./components/TablePagination"
import { classNames } from "../../../utils/classNames"
import { ScrollIndicators } from "./components/ScrollIndicators"

type Props<TRowData extends TableRowData> = {
    columns: TableColumn<TRowData>[]
    data: TRowData[]
    activeRowId?: Maybe<string>
    noDataLabel?: string
    size?: TableSize
    style?: TableStyle
    isLoading?: boolean // If true, the table will render a loading overlay instead of the table body.
    isLoadingOverlay?: boolean // If true, the table will render loading overlay on top of possible existing data
    loadingMessage?: Maybe<string>
    loadingMode?: "default" | "skeletons"
    rowSelectVisibility?: TableRowSelectVisibility
    virtualization?: TableVirtualizationConfig
    bordered?: boolean
    rowsHighlighting?: boolean
    wrapClassName?: string // For styling the container
    tableWrapClassName?: string // For styling border of table wrap <div />
    bodyRowClassName?: string // For styling table body <tr /> row elements
    headerRowClassName?: string // For styling table header <tr /> row element
    footerRowClassName?: string // For styling table footer <tr /> row element
    onRowClick?: (data: TRowData) => void
    onRowClassName?: (data: TRowData, row: Row<TRowData>) => Maybe<string> // For dynamically styling table body <tr /> row elements based on row data
    showPagination?: boolean
    // If we want to set sorting, expanded, row selection, or pagination state externally,
    // we can pass a custom state object with the necessary properties.
    customState?: {
        sorting?: TableSortingState
        expanded?: TableExpandedState
        rowSelection?: TableRowSelectionState
        pagination?: TablePaginationState
    }
    // Default state values for sorting, expanded rows, row selection, and pagination.
    // Please note that these will be overridden if `customState` is provided.
    defaultSort?: SortingState
    defaultExpanded?: ExpandedState
    defaultRowSelection?: RowSelectionState
    defaultPagination?: PaginationState
}

/** Table component for displaying rows of data. */
export const Table = <TRowData extends TableRowData>({
    columns: columnsProp,
    data,
    activeRowId,
    noDataLabel = "No data for selected range",
    size = "normal",
    style = "normal",
    isLoading,
    isLoadingOverlay,
    loadingMessage = "Loading data...",
    loadingMode = "default",
    rowSelectVisibility = TableRowSelectVisibility.Hover,
    virtualization,
    bordered,
    rowsHighlighting,
    wrapClassName,
    tableWrapClassName,
    bodyRowClassName,
    headerRowClassName,
    footerRowClassName,
    onRowClick,
    onRowClassName,
    showPagination,
    customState,
    defaultSort,
    defaultExpanded,
    defaultRowSelection,
    defaultPagination,
}: Props<TRowData>) => {
    const hasData = data.length > 0 && !isLoading
    const columns = useMemo(() => mapColumnDefs(columnsProp, data), [columnsProp, data])

    const [sorting, setSorting] = useState<SortingState>(defaultSort ?? [])
    const [expanded, setExpanded] = useState<ExpandedState>(defaultExpanded ?? {})
    const [rowSelection, setRowSelection] = useState<RowSelectionState>(defaultRowSelection ?? {})
    const [pagination, setPagination] = useState<PaginationState>(defaultPagination ?? { pageIndex: 0, pageSize: 100 })

    // If custom state is provided, use it; otherwise, use local state
    const sortingState = customState?.sorting ?? { sorting: sorting, setSorting: setSorting }
    const expandedState = customState?.expanded ?? { expanded: expanded, setExpanded: setExpanded }
    const rowSelectionState = customState?.rowSelection ?? {
        rowSelection: rowSelection,
        setRowSelection: setRowSelection,
    }
    const paginationState = customState?.pagination
        ? {
              pagination: customState.pagination.pagination,
              setPagination: customState.pagination.setPagination,
          }
        : {
              pagination: pagination,
              setPagination: setPagination,
          }

    const hasCustomSorting = !!customState?.sorting

    const renderSkeletons = isLoading && loadingMode === "skeletons"
    const skeletonsData = useMemo(() => Array(8).fill({}), [])
    const shouldRenderFooter = useMemo(() => columns.some(c => !!c.footer), [columns])

    const table = useReactTable({
        data: renderSkeletons ? skeletonsData : data,
        columns: columns,
        state: {
            sorting: sortingState.sorting,
            expanded: expandedState.expanded,
            rowSelection: rowSelectionState.rowSelection,
            pagination: paginationState.pagination,
        },
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row: TRowData) => row.id,
        getSubRows: (row: TRowData) => row.children ?? undefined,
        // Sorting config
        onSortingChange: updaterOrValue =>
            sortingState.setSorting(functionalUpdate(updaterOrValue, sortingState.sorting)),
        ...(hasCustomSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() }),
        // Expanded rows config
        onExpandedChange: updaterOrValue =>
            expandedState.setExpanded(functionalUpdate(updaterOrValue, expandedState.expanded)),
        getExpandedRowModel: getExpandedRowModel(),
        // Row selection config
        enableRowSelection: (row: Row<TRowData>) => !!row.original.isSelectable,
        onRowSelectionChange: updaterOrValue =>
            rowSelectionState.setRowSelection(functionalUpdate(updaterOrValue, rowSelectionState.rowSelection)),
        // Pagination config
        onPaginationChange: updaterOrValue =>
            paginationState.setPagination(functionalUpdate(updaterOrValue, paginationState.pagination)),
        ...(customState?.pagination
            ? {
                  manualPagination: true,
                  pageCount: Math.ceil(customState.pagination.totalCount / customState.pagination.pagination.pageSize),
              }
            : showPagination && { getPaginationRowModel: getPaginationRowModel() }),
    })

    // Set table metadata, which can be then accessed easily in child components via `table.options.meta`
    const hasSelectableRows = useMemo(() => hasTableAnySelectableRows(data), [data])
    table.setOptions({
        ...table.options,
        meta: {
            hasExpandableRows: table.getCanSomeRowsExpand(),
            hasSelectableRows: hasSelectableRows,
            rowSelectVisibility: rowSelectVisibility,
        },
    })

    const wrapDivRef = useRef<HTMLDivElement>(null)
    const tableBodyWrapRef = useRef<HTMLDivElement>(null)

    const renderContent = () => {
        if (hasData || renderSkeletons) {
            return (
                <TableBody
                    style={style}
                    size={size}
                    table={table}
                    onRowClick={onRowClick}
                    onRowClassName={onRowClassName}
                    activeRowId={activeRowId}
                    rowsHighlighting={rowsHighlighting}
                    rowClassName={bodyRowClassName}
                    bordered={bordered}
                    renderSkeletons={renderSkeletons}
                    tableBodyWrapRef={tableBodyWrapRef}
                    virtualization={virtualization}
                />
            )
        }
        if (isLoading) {
            return (
                <tbody>
                    <tr className={classes.loading.row}>
                        <td className={classes.loading.cell} colSpan={columnsProp.length}>
                            <LoadingOverlay size="default" caption={loadingMessage} opacity={0.8} />
                        </td>
                    </tr>
                </tbody>
            )
        }
        if (!hasData) {
            return (
                <tbody>
                    <tr className={classes.noData.row}>
                        <td className={classes.noData.cell} colSpan={columnsProp.length}>
                            {noDataLabel}
                        </td>
                    </tr>
                </tbody>
            )
        }
        return null
    }

    return (
        <div className={classNames(classes.wrap, wrapClassName)}>
            <div
                ref={wrapDivRef}
                className={classNames(classes.tableWrap, classes.tableWrapStyle[style], tableWrapClassName)}>
                {isLoadingOverlay ? (
                    <LoadingOverlay size="default" opacity={0.8} className="justify-start pt-28" />
                ) : null}
                <table className={classNames(classes.table, virtualization?.isEnabled && classes.tableVirtualized)}>
                    <TableHeader
                        table={table}
                        size={size}
                        style={style}
                        rowClassName={headerRowClassName}
                        bordered={bordered}
                        virtualized={virtualization?.isEnabled}
                    />
                    {virtualization?.isEnabled ? (
                        <div
                            ref={tableBodyWrapRef}
                            style={{
                                overflowY: "auto",
                                width: "100%",
                                height: virtualization.containerHeight,
                            }}>
                            {renderContent()}
                        </div>
                    ) : (
                        renderContent()
                    )}
                    {shouldRenderFooter ? (
                        <TableFooter
                            table={table}
                            size={size}
                            rowClassName={footerRowClassName}
                            bordered={bordered}
                            virtualized={virtualization?.isEnabled}
                        />
                    ) : null}
                </table>
            </div>
            <ScrollIndicators wrapDivRef={wrapDivRef} />
            {showPagination && hasData ? <TablePagination table={table} scrollTargetRef={wrapDivRef} /> : null}
        </div>
    )
}

const classes = {
    wrap: "relative rounded-md overflow-hidden",
    tableWrap: "relative overflow-x-auto rounded-md scroll-mt-[180px]",
    tableWrapStyle: {
        normal: "border border-gray-200/80",
        dashed: "border border-gray-200/80",
        legacy: "border-b border-gray-200/80",
    },
    table: "min-w-full w-full text-black/80 table-fixed",
    tableVirtualized: "grid",
    loading: { row: "h-[140px] border-t border-gray-200/80", cell: "relative" },
    noData: { row: "h-[60px] border-t border-gray-200/80", cell: "text-center text-black/50" },
}
